import {
  MapPin,
  Leaf,
  UtensilsCrossed,
  Mountain,
  Award,
  Shield,
  Recycle,
  CreditCard,
  Wine,
  Gamepad2,
  Dumbbell,
  Briefcase,
  Coffee,
  Eye,
} from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

const USPStrip = () => {
  const { t } = useLanguage();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const highlights = [
    {
      icon: Wine,
      title: t("features.elegantBarLounge"),
      description: t("features.elegantBarLoungeDesc"),
      image:
        "/images/Homepage%20Shortlisted%20media/USP%20strip/bar%20and%20lounge.webp",
    },
    {
      icon: Gamepad2,
      title: t("features.recreationGames"),
      description: t("features.recreationGamesDesc"),
      image:
        "/images/Homepage%20Shortlisted%20media/USP%20strip/billiar%20room.webp",
    },
    {
      icon: Dumbbell,
      title: t("features.technogymFitness"),
      description: t("features.technogymFitnessDesc"),
      image:
        "/images/Homepage%20Shortlisted%20media/USP%20strip/fitness%20centre.webp",
    },
    {
      icon: Briefcase,
      title: t("features.businessFacilities"),
      description: t("features.businessFacilitiesDesc"),
      image:
        "/images/Homepage%20Shortlisted%20media/USP%20strip/conference%20room.webp",
    },
    {
      icon: Eye,
      title: t("features.panoramicViews"),
      description: t("features.panoramicViewsDesc"),
      image:
        "/images/Homepage%20Shortlisted%20media/USP%20strip/views.webp",
    },
    {
      icon: Coffee,
      title: t("features.alpineBistro"),
      description: t("features.alpineBistroDesc"),
      image:
        "/images/Homepage%20Shortlisted%20media/USP%20strip/bistro.webp",
    },
    {
      icon: Leaf,
      title: t("features.integratedWellness"),
      description: t("features.integratedWellnessDesc"),
      image: "/images/activity arcade/Bento Grid/nutraswiss spa.webp",
    },
    {
      icon: CreditCard,
      title: t("features.guestCardBenefits"),
      description: t("features.guestCardBenefitsDesc"),
      image:
        "/images/Homepage%20Shortlisted%20media/USP%20strip/Guest%20Card.usp-640x768.webp",
    },
    {
      icon: Shield,
      title: t("features.gastrosuisseExcellence"),
      description: t("features.gastrosuisseExcellenceDesc"),
      image:
        "/images/Homepage%20Shortlisted%20media/USP%20strip/IMG-20250812-WA0013.usp-640x768.webp",
    },
    {
      icon: Recycle,
      title: t("features.swisstainableCommitment"),
      description: t("features.swisstainableCommitmentDesc"),
      image: "/images/local_attractions/alpine_hiking_trails.usp-640x768.webp",
    },
    {
      icon: UtensilsCrossed,
      title: t("features.heritageCuisine"),
      description: t("features.heritageCuisineDesc"),
      image: "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5178.optimized.webp",
    },
    {
      icon: MapPin,
      title: t("features.acreResort"),
      description: t("features.acreResortDesc"),
      image:
        "/images/Homepage%20Shortlisted%20media/USP%20strip/a_building_with_trees_in_front_of_it_at_Parkhotel__89.usp-640x768.webp",
    },
    {
      icon: Award,
      title: t("features.authenticHeritage"),
      description: t("features.authenticHeritageDesc"),
      image:
        "/images/Homepage%20Shortlisted%20media/USP%20strip/in%201881.usp-640x768.webp",
    },
    {
      icon: Mountain,
      title: t("features.alpineExperiences"),
      description: t("features.alpineExperiencesDesc"),
      image:
        "/images/local_attractions/paragliding_interlaken.usp-640x768.webp",
    },
  ];

  // Track active card based on scroll position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateActiveIndex = () => {
      const scrollLeft = container.scrollLeft;
      const containerWidth = container.clientWidth;
      const viewportCenter = scrollLeft + containerWidth / 2;
      
      // Get all card elements
      const cards = container.querySelectorAll('[data-oid="q5.zhyn"]');
      if (cards.length === 0) return;

      let closestIndex = 0;
      let closestDistance = Infinity;

      // Find the card whose center is closest to the viewport center
      cards.forEach((card, index) => {
        const cardRect = card.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const cardLeft = cardRect.left - containerRect.left + scrollLeft;
        const cardWidth = cardRect.width;
        const cardCenter = cardLeft + cardWidth / 2;
        const distance = Math.abs(cardCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      // Handle edge cases: if scrolled to start, use first card; if scrolled to end, use last card
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (scrollLeft <= 10) {
        setActiveIndex(0);
      } else if (scrollLeft >= maxScroll - 10) {
        setActiveIndex(highlights.length - 1);
      } else {
        setActiveIndex(closestIndex);
      }
    };

    container.addEventListener("scroll", updateActiveIndex);
    updateActiveIndex(); // Initial calculation

    // Also update on resize
    window.addEventListener("resize", updateActiveIndex);

    return () => {
      container.removeEventListener("scroll", updateActiveIndex);
      window.removeEventListener("resize", updateActiveIndex);
    };
  }, [highlights.length]);

  return (
    <section
      id="about"
      className="relative pt-8 pb-16 bg-gradient-to-b from-elegant-white via-pebble-beige to-pebble-beige z-30 overflow-visible"
      data-oid="6yw5:m0"
    >
      <div className="w-full overflow-visible" data-oid="_zgr1ne">
        {/* Bottom gradient transition */}
        <div
          className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-b from-pebble-beige to-elegant-white z-10"
          data-oid="izxvm_e"
        ></div>
        <div
          ref={scrollContainerRef}
          className={`flex gap-6 overflow-x-auto overflow-y-visible pt-12 pb-8 scrollbar-hide px-4 sm:px-6 lg:px-8 select-none relative z-20 ${
            isDragging ? "cursor-grabbing" : "cursor-grab"
          }`}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          data-oid="a6fdmwm"
        >
          {highlights.map((highlight, index) => {
            const Icon = highlight.icon;
            return (
              <div
                key={index}
                className="group cursor-pointer flex-shrink-0 relative z-50 mt-8 mb-2"
                data-oid="q5.zhyn"
              >
                {/* Card Container */}
                <div
                  className="relative w-80 h-96 rounded-2xl overflow-hidden transition-transform duration-300 group-hover:-translate-y-14 z-50"
                  style={{
                    backgroundImage: `url("${highlight.image.includes('%') ? highlight.image : encodeURI(highlight.image)}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: highlight.image.includes("bar") && highlight.image.includes("lounge")
                      ? "20% center"
                      : "center center",
                    backgroundRepeat: 'no-repeat',
                  }}
                  data-oid="13lmvwv"
                >
                  {/* Gradient Overlay - Subtle for text readability */}
                  <div
                    className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-transparent"
                    data-oid="he0qoc8"
                  ></div>

                  {/* Title */}
                  <div
                    className="absolute top-0 left-0 right-0 p-6"
                    data-oid="mblnqn8"
                  >
                    <h3
                      className="font-playfair font-semibold text-elegant-white text-lg leading-tight"
                      data-oid="7uh9iaj"
                    >
                      {highlight.title}
                    </h3>
                  </div>

                  {/* Icon */}
                  <div
                    className="absolute bottom-4 right-4 w-10 h-10 bg-elegant-white/40 rounded-full flex items-center justify-center backdrop-blur-sm"
                    data-oid="3xxcwz1"
                  >
                    <Icon
                      className="text-gray-900"
                      size={20}
                      data-oid="-zc3bp1"
                    />
                  </div>
                </div>

                {/* Description Card (Hidden by default, slides down on hover) */}
                <div
                  className="absolute bottom-0 left-0 right-0 bg-elegant-white rounded-b-2xl shadow-lg px-6 pt-6 pb-3 transform translate-y-0 opacity-100 transition-all duration-300 z-40"
                  data-oid="njzhl75"
                >
                  <p
                    className="font-lato text-gray-900 text-sm leading-relaxed"
                    data-oid="khle0g4"
                  >
                    {highlight.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Scroll Indicators */}
        <div
          className="flex justify-center items-center mt-2.5 space-x-2"
          data-oid="_dvplnc"
        >
          <div className="flex space-x-2" data-oid="zoo4gaa">
            {highlights.map((_, index) => (
              <div
                key={index}
                className={`rounded-full transition-all duration-300 ${
                  index === activeIndex
                    ? "w-2.5 h-2.5 bg-gray-900"
                    : "w-2 h-2 bg-gray-900/30"
                }`}
                data-oid="x--xc7f"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default USPStrip;
